import { useWSModalCustom } from "@wingspanhq/fe-component-library";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces/api/requirement";
import { ISharedFileRequestResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { WSQueryCache, WSQueryKey } from "@ws-react-query";
import { useCallback } from "react";
import { QUERY_BACKGROUND_CHECKS_REQUEST } from "../../query/backgroundChecks/queries/useBackgroundChecksRequestQuery";
import { QUERY_IDENTITY_VERIFICATION_REQUEST } from "../../query/identityVerification/keys";
import { QUERY_INSURANCE_COVERAGE_MONITOR } from "../../query/insuranceCoverageMonitor/queries/useInsuranceCoverageMonitorQuery";
import { QUERY_NOTIFICATIONS_NOTIFICATIONS } from "../../query/notifications/keys";
import {
  QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
  QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL
} from "../../query/payeeEngagements/keys";
import { QUERY_PAYER_ENGAGEMENT } from "../../query/payerEngagements/keys";
import {
  QUERY_REQUIREMENT,
  QUERY_REQUIREMENTS_FULL_LIST,
  QUERY_REQUIREMENTS_FULL_LIST_SIZE
} from "../../query/requirements/keys";
import { QUERY_LIST_RESOURCE_EVENTS } from "../../query/resourceEvents/queries/useQueryListEvents";
import { QUERY_ALL_PAYEE_ROWS } from "../../query/search/payee/queries/useQueryAllPayeeRows";
import { QUERY_PAYER_ROWS_ALL } from "../../query/search/payer/queries/useQueryPayerRowsAll";
import { QUERY_SHARED_FILE } from "../../query/sharedFile/keys";
import { QUERY_SIGNED_DOCUMENT } from "../../query/signedDocuments/keys";
import { InsuranceCoverageMonitor } from "../../services/api/insurance/monitor/types";
import { IS_LOCAL_ENV } from "../../shared/constants/environment";
import { QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS } from "../Onboarding/queries/useQueryAllPayersWithEngagements";
import { ModalRequirementHistory } from "./components/ModalRequirementHistory";
import { extendRequirementOnboardingToolbarActions } from "./helpers/extendRequirementOnboardingToolbarActions";
import { getRequirementHistoryWidget } from "./helpers/getRequirementHistoryWidget";
import { getRequirementOverrideStatusAlert } from "./helpers/getRequirementOverrideStatusAlert";
import { getRequirementProgressWidget } from "./helpers/getRequirementProgressWidget";
import { useBackgroundChecksRequirementTableConfig } from "./modules/BackgroundChecks";
import { useIdentityVerificationRequirementConfig } from "./modules/IdentityVerification";
import { useInsuranceCoverageRequirementTableConfig } from "./modules/InsuranceCoverage";
import { selectorPolicyTypesFromSettings } from "./modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";
import { usePayoutMethodRequirementTableConfig } from "./modules/PayoutMethod";
import { useRegistrationRequirementTableConfig } from "./modules/Registration";
import { useSharedFileRequirementTableConfig } from "./modules/SharedFile";
import { useSignatureRequirementTableConfig } from "./modules/Signature";
import { useTaxVerificationRequirementTableConfig } from "./modules/TaxVerification";
import {
  ContextItem,
  RequirementAction,
  RequirementContextType,
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "./types";
import { ALL_REQUIREMENT_EVENTS, RequirementEvent } from "./utils";

export type RequirementsConfigParams = {
  requirements: Array<RequirementTableItemData>;
  contextType: RequirementContextType;
  context?: ContextItem;
};

export type RequirementsConfigResult = ReturnType<typeof useRequirementsConfig>;

export const useRequirementsConfig = (props: RequirementsConfigParams) => {
  const { contextType, context = {}, requirements } = props;
  const modalHistory = useWSModalCustom(ModalRequirementHistory);

  const callback: RequirementTableCallback = useCallback(
    (eventName, requirement) => {
      const eventsWithNoSideEffects: RequirementEvent[] = [
        ALL_REQUIREMENT_EVENTS.DownloadAsPayee,
        ALL_REQUIREMENT_EVENTS.DownloadAsPayer
      ];

      if (!eventsWithNoSideEffects.includes(eventName)) {
        const listOfQueries: WSQueryKey[] = [];
        const addToInvalidations = (key: WSQueryKey) => listOfQueries.push(key);

        if ("dataSourceId" in requirement && requirement.dataSourceId) {
          switch (requirement.requirementType) {
            case RequirementType.Signature:
              addToInvalidations([
                QUERY_SIGNED_DOCUMENT,
                requirement.dataSourceId
              ]);
              break;
            case RequirementType.SharedFile:
              addToInvalidations([QUERY_SHARED_FILE, requirement.dataSourceId]);
              break;
            case RequirementType.BackgroundCheck:
              addToInvalidations([
                QUERY_BACKGROUND_CHECKS_REQUEST,
                requirement.dataSourceId
              ]);
              break;
            case RequirementType.IdentityVerification:
              addToInvalidations([
                QUERY_IDENTITY_VERIFICATION_REQUEST,
                requirement.dataSourceId
              ]);
              break;
            case RequirementType.InsuranceCoverage:
              addToInvalidations([
                QUERY_INSURANCE_COVERAGE_MONITOR,
                requirement.dataSourceId
              ]);
              break;
          }
        }

        addToInvalidations([QUERY_NOTIFICATIONS_NOTIFICATIONS]);
        addToInvalidations([QUERY_REQUIREMENTS_FULL_LIST]);
        addToInvalidations([QUERY_REQUIREMENTS_FULL_LIST_SIZE]);
        addToInvalidations([QUERY_REQUIREMENT, requirement.requirementId]);

        addToInvalidations([QUERY_PAYER_ROWS_ALL]);
        addToInvalidations([QUERY_ALL_PAYEE_ROWS]);

        addToInvalidations([QUERY_PAYER_ENGAGEMENT]);
        addToInvalidations([QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS]);
        addToInvalidations([QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL]);
        addToInvalidations([QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID]);

        addToInvalidations([QUERY_LIST_RESOURCE_EVENTS]);

        if (IS_LOCAL_ENV) {
          console.log(
            "Invalidating queries",
            JSON.stringify(listOfQueries, null, 2)
          );
        }

        listOfQueries.forEach(key => WSQueryCache.invalidateQueries(key));
      }
    },
    []
  );

  const signatureConfig = useSignatureRequirementTableConfig(
    callback,
    requirements
  );
  const sharedFileConfig = useSharedFileRequirementTableConfig(
    callback,
    requirements
  );
  const backgroundCheckConfig = useBackgroundChecksRequirementTableConfig(
    callback,
    requirements
  );
  const identityVerificationConfig = useIdentityVerificationRequirementConfig(
    callback,
    requirements
  );
  const insuranceCoverageConfig = useInsuranceCoverageRequirementTableConfig(
    callback,
    requirements
  );
  const registrationConfig = useRegistrationRequirementTableConfig(
    callback,
    requirements
  );
  const taxVerificationConfig = useTaxVerificationRequirementTableConfig(
    callback,
    requirements
  );
  const payoutMethodConfig = usePayoutMethodRequirementTableConfig(
    callback,
    requirements
  );

  const config: RequirementTableRowConfig = {
    [RequirementType.Signature]: signatureConfig,
    [RequirementType.SharedFile]: sharedFileConfig,
    [RequirementType.BackgroundCheck]: backgroundCheckConfig,
    [RequirementType.IdentityVerification]: identityVerificationConfig,
    [RequirementType.InsuranceCoverage]: insuranceCoverageConfig,
    [RequirementType.Registration]: registrationConfig,
    [RequirementType.TaxVerification]: taxVerificationConfig,
    [RequirementType.PayoutMethod]: payoutMethodConfig
  };

  const isLoading = Object.values(config).some(({ isLoading }) => isLoading);
  const dataUpdatedAt = Object.values(config)
    .map(({ dataUpdatedAt }) => dataUpdatedAt)
    .join(", ");

  return {
    isActionRequired: (item: RequirementTableItemData) => {
      return config[item.requirementType].isActionRequired?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getRequirementDetailsWidgets(item: RequirementTableItemData) {
      return config[item.requirementType].requirementDetailsWidgets?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getRequirementDetailsProgressWidget(
      item: RequirementTableItemData,
      showTitle: boolean = true
    ) {
      const steps = config[
        item.requirementType
      ].requirementDetailsProgressWidget?.[contextType](
        item as IRequirementResponse,
        context
      );

      return getRequirementProgressWidget(steps, item, showTitle);
    },
    getRequirementDetailsHistoryWidget(item: RequirementTableItemData) {
      return getRequirementHistoryWidget(item);
    },
    getRequirementInstructionsWidgets(item: RequirementTableItemData) {
      return config[item.requirementType].requirementInstructionsWidgets?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getRequirementInstructions(item: RequirementTableItemData) {
      return config[item.requirementType].requirementInstructions?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getRequirementName(item: RequirementTableItemData) {
      return config[item.requirementType].requirementName?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getToolbarActions(item: RequirementTableItemData) {
      return config[item.requirementType].toolbarActions?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getOnboardingToolbarActions(item: RequirementTableItemData) {
      return (onClose: () => void) => {
        let actions: RequirementAction[] = [];

        const isMainActionActive = (action: RequirementAction) =>
          action.mainAction &&
          action.visible !== false &&
          action.hidden !== true &&
          action.noOnboarding !== true &&
          action.buttonKind;

        const originalActions =
          config[item.requirementType].toolbarActions?.[contextType](
            item as IRequirementResponse,
            context
          ) || [];

        actions = extendRequirementOnboardingToolbarActions(
          item,
          originalActions,
          onClose
        );

        const isMainActionExist = actions.some(isMainActionActive);
        const activeActions = actions.filter(
          action =>
            action.noOnboarding !== true &&
            action.visible !== false &&
            action.hidden !== true
        );

        return activeActions
          .map((action, index, all) => ({
            ...action,
            buttonKind: action.buttonKind
              ? isMainActionExist
                ? isMainActionActive(action)
                  ? action.buttonKind
                  : "Link"
                : action.buttonKind
              : undefined
          }))
          .map(action =>
            isLoading
              ? {
                  ...action,
                  visible: false
                }
              : action
          ) as RequirementAction[];
      };
    },
    getRequirementStatusAlert(requirement: RequirementTableItemData) {
      const approvalFlowAlert = getRequirementOverrideStatusAlert({
        requirement,
        contextType,
        onOpenHistory() {
          modalHistory.open({
            requirement
          });
        }
      });

      if (approvalFlowAlert) {
        return approvalFlowAlert;
      }

      return config[requirement.requirementType].requirementStatusAlert?.[
        contextType
      ](requirement as IRequirementResponse, context);
    },
    getRequirementBasicInformation(item: RequirementTableItemData) {
      return config[item.requirementType].requirementBasicInformation?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getRequirementDetails(item: RequirementTableItemData) {
      return config[item.requirementType].requirementDetails?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getRequirementAdditionalContent(item: RequirementTableItemData) {
      return config[item.requirementType].requirementAdditionalContent?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getNameCellProps(item: RequirementTableItemData) {
      return config[item.requirementType].nameCell[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getTypeCellProps(item: RequirementTableItemData) {
      return config[item.requirementType].typeCell[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getStatusCellProps(item: RequirementTableItemData) {
      return config[item.requirementType].statusCell[contextType](
        item as IRequirementResponse,
        context
      );
    },
    renderRowMenuActions(item: RequirementTableItemData) {
      const originalActions =
        config[item.requirementType].toolbarActions?.[contextType](
          item as IRequirementResponse,
          context
        ) || [];

      return originalActions.filter(action => action.noTable !== true);
    },
    isLoading,
    dataUpdatedAt,
    getContextType(item: RequirementTableItemData) {
      const dataSource = config[item.requirementType].getAdditionalData?.(
        item as IRequirementResponse,
        context
      ) as ISharedFileRequestResponse | undefined;

      return dataSource?.contextType;
    },
    getPolicyType(item: RequirementTableItemData) {
      const dataSource = config[item.requirementType].getAdditionalData?.(
        item as IRequirementResponse,
        context
      ) as InsuranceCoverageMonitor | undefined;

      return selectorPolicyTypesFromSettings(dataSource?.settings);
    },
    getAdditionalData(item: RequirementTableItemData) {
      return config[item.requirementType].getAdditionalData?.(
        item as IRequirementResponse,
        context
      );
    }
  } as const;
};
